import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {filter, first, map, switchMap} from 'rxjs/operators';
import {BrandColor, BrandDesign, OrganizationMembershipRole} from '@paperlessio/sdk/api/models';
import {Subscription} from 'rxjs';
import {AuthenticationStore, BrandDesignStore, CurrentWorkspaceStore} from '@paperlessio/sdk/api/stores';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {PresetExtractNamePipe} from '@shared/controls/color-input/preset-extract-name.pipe';
import {TinyColor} from '@ctrl/tinycolor';
import {Color, HSL, HSLAsource, HSV, HSVAsource, RGB, toState} from 'ngx-color';

@Component({
  selector: 'pl-color-panel',
  templateUrl: './color-panel.component.html',
  styleUrls: ['./color-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPanelComponent),
      multi: true
    }
  ]
})
export class ColorPanelComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private subs = new Subscription();
  private _value = '';
  private oldHue: number;

  presetActive = false;
  presetValue = '';
  presets: BrandColor[] = [];
  showEditButton: boolean;
  organizationId: number;

  hsl: HSL;
  hsv: HSV;

  @Input() disabled: boolean;
  @Input() defaultValue = '#fff';
  @Input() showResetButton = true;
  @Input() showPresets = true;

  @ViewChild('hexInput') hexInput: ElementRef;
  @ViewChild('r') r: ElementRef;
  @ViewChild('g') g: ElementRef;
  @ViewChild('b') b: ElementRef;

  get pickerColor(): string {
    if (this.presetActive) {
      return this.presetValue;
    } else {
      return this.value;
    }
  }

  get canShowResetButton(): boolean {
    if (!this.showResetButton) {
      return false;
    }

    if (this.value.indexOf('#') < 0) {
      return true;
    }

    const defaultColor = new TinyColor(this.defaultValue || '#fff');
    const currentColor = new TinyColor(this.value);

    return defaultColor.toHex8() !== currentColor.toHex8();
  }

  get hex() {
    return new TinyColor(this.presetActive ? this.presetValue : this.hsl).toHex();
  }

  get rgb() {
    return new TinyColor(this.hex).toRgb();
  }

  get activeBackground() {
    return `rgb(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b})`;
  }

  @Input() set value(v: string) {
    this._value = v;
    this.setColor(v);
  }

  get value() {
    return this._value;
  }

  get valueHasChanged(): boolean {
    return !new TinyColor(`#${this.hex}`).equals(this.value);
  }

  constructor(private designStore: BrandDesignStore,
              private cdRef: ChangeDetectorRef,
              private presetExtractNamePipe: PresetExtractNamePipe,
              private authStore: AuthenticationStore,
              private currentWorkspaceStore: CurrentWorkspaceStore) { }

  ngOnInit(): void {
    this.setColor(this.value);

    if (this.showPresets) {
      this.subs.add(this.designStore.design
        .pipe(filter((x: BrandDesign) => !!x?.brand_styles), first())
        .subscribe((design: BrandDesign) => {
          this.presets = design.brand_styles?.colors;
        }));
    }

    this.subs.add(this.currentWorkspaceStore.membership
      .pipe(
        filter(mem => mem?.workspace?.organization_id != null),
        map(mem => mem.workspace?.organization_id),
        switchMap(orgId => {
          return this.authStore.currentOrganizationMemberships
            .pipe(map(mems => mems.find(mem => mem.organization_id === orgId)))
        })
      )
      .subscribe(membership => {
        const role = membership.role;
        this.organizationId = membership.organization_id;
        this.showEditButton = role === OrganizationMembershipRole.owner || role === OrganizationMembershipRole.admin;
      }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onChange = (value: string) => {};
  onTouched = () => {};

  rawColorChangedHex(value: string) {
    this.setColor(`#${value}`);
  }

  rawColorChangedRgb(rgb: Partial<RGB>) {
    this.hsl = new TinyColor({ ...this.rgb, ...rgb }).toHsl();
    this.hsv = new TinyColor({ ...this.rgb, ...rgb }).toHsv();
  }

  rawColorChangedHsla(event: {data: HSLAsource, $event: Event}) {
    this.rawColorChanged(event);
  }

  rawColorChangedHsva(event: {data: HSVAsource, $event: Event}) {
    this.rawColorChanged(event);
  }

  rawColorChanged(event: {data: HSLAsource | HSVAsource, $event: Event}) {
    this.setColor(event.data);
  }

  getPreset(v: string): BrandColor {
    const foundPreset = this.presets.find(p => {
      return p.name.toLowerCase() === this.presetExtractNamePipe.transform(v).toLowerCase() ||
        p.value.toLowerCase() === v.toLowerCase();
    });

    return foundPreset;
  }

  setColor(value: string | HSLAsource | HSVAsource | RGB) {
    let valueToSet = value;
    const isObject = (value as HSLAsource)?.source || (value as RGB)?.r != null || (value as RGB)?.g != null || (value as RGB)?.b != null;

    this.presetActive = false;
    this.presetValue = null;

    if (!isObject) {
      const preset = this.getPreset(value as string);
      if (preset != null) {
        valueToSet = preset.value;
      }
    }

    // The method toState is also used by the ngx-color framework itself.
    // In combination with the saving of the old hue, the color picking now works flawlessly.
    const state: Color = toState(valueToSet, this.oldHue, true);
    this.hsl = state.hsl;
    this.hsv = state.hsv;
    this.oldHue = state.oldHue;
    this.cdRef.markForCheck();
  }

  applyColorChange(source?: 'hex' | 'r' | 'g' | 'b') {
    if (!this.valueHasChanged) {
      return;
    }

    this.changeValue(`#${this.hex}`);

    switch (source) {
      case 'r': {
        this.r.nativeElement.focus();
        break;
      }
      case 'g': {
        this.g.nativeElement.focus();
        break;
      }
      case 'b': {
        this.b.nativeElement.focus();
        break;
      }
      case 'hex': {
        this.hexInput.nativeElement.focus();
        break;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writePreset(brandColor: BrandColor): void {
    this.presetActive = true;
    this.presetValue = brandColor.value;
    this.changeValue(`var(--design-${brandColor.name})`);
  }

  writeValue(value: string): void {
    this.value = value || '';
    this.cdRef.markForCheck();
  }

  changeValue(value: string): void {
    if (this.disabled) {
      return;
    }

    this.writeValue(value);

    this.onChange(value);
    this.onTouched();
  }
}
